<template>
  <div class="form-fields-group">
    <b-row>
      <b-col
        v-for="field in formFields"
        :key="`col-${field.name}-${field.type}`"
        :md="field.col || (field.type === 'textarea' ? 12 : 6)"
      >
        <!-- Text and Email Fields -->
        <b-form-group
          v-if="field.type === 'text' || field.type === 'number' || field.type === 'email'"
          :label="field.label"
          :label-for="field.id || field.name"
          :description="field.description"
        >
          <template #label>
            {{ field.label }} <span v-if="field.required" class="required-indicator">*</span>
          </template>
          <validation-provider
            :key="`${field.name}-${field.type}`"
            :name="field.name"
            :vid="field.name"
            :rules="field.rules || (field.required ? 'required' : '')"
            v-slot="{ errors, validate }"
          >
            <b-form-input
              v-if="field.vmask"
              :id="field.id || field.name"
              :name="field.name"
              :type="field.type"
              v-model="localValues[field.name]"
              :placeholder="field.placeholder || field.label"
              :state="errors.length > 0 ? false : null"
              :pattern="field.pattern || ''"
              v-mask="field.vmask || null"
              @blur="validate"
              @input="emitInput"
            />
            <b-form-input
              v-else
              :id="field.id || field.name"
              :name="field.name"
              :type="field.type"
              v-model="localValues[field.name]"
              :placeholder="field.placeholder || field.label"
              :state="errors.length > 0 ? false : null"
              :pattern="field.pattern || ''"
              @blur="validate"
              @input="emitInput"
            />
            <b-form-invalid-feedback v-if="errors.length > 0">{{
              errors[0]
            }}</b-form-invalid-feedback>
          </validation-provider>
        </b-form-group>

        <!-- Textarea Field -->
        <b-form-group
          v-else-if="field.type === 'textarea'"
          :label="field.label"
          :label-for="field.id || field.name"
        >
          <template #label>
            {{ field.label }} <span v-if="field.required" class="required-indicator">*</span>
          </template>
          <validation-provider
            :key="`${field.name}-${field.type}`"
            :name="field.name"
            :vid="field.name"
            :rules="field.rules || (field.required ? 'required' : '')"
            v-slot="{ errors, validate }"
          >
            <b-form-textarea
              :id="field.id || field.name"
              :name="field.name"
              :type="field.type"
              v-model="localValues[field.name]"
              :placeholder="field.label"
              :state="errors.length > 0 ? false : null"
              :pattern="field.pattern || ''"
              @blur="validate"
              @input="emitInput"
            />
            <b-form-invalid-feedback v-if="errors.length > 0">{{
              errors[0]
            }}</b-form-invalid-feedback>
          </validation-provider>
        </b-form-group>

        <!-- Date Field -->
        <b-form-group
          v-else-if="field.type === 'date'"
          :label="field.label"
          :label-for="field.id || field.name"
        >
          <template #label>
            {{ field.label }} <span v-if="field.required" class="required-indicator">*</span>
          </template>
          <validation-provider
            :key="`${field.name}-date`"
            :name="field.label"
            :vid="field.name"
            :rules="field.rules || (field.required ? 'required' : '')"
            v-slot="{ errors }"
          >
            <flat-pickr
              :id="field.id || field.name"
              :name="field.name"
              v-model="localValues[field.name]"
              :config="dateConfig"
              class="form-control"
              placeholder="YYYY-MM-DD"
              :class="{ 'is-invalid': errors.length > 0 }"
              @input="emitInput"
            />
            <div v-if="errors.length > 0" class="invalid-feedback d-block">{{ errors[0] }}</div>
          </validation-provider>
        </b-form-group>

        <!-- Select Field -->
        <b-form-group
          v-else-if="field.type === 'select'"
          :label="field.label"
          :label-for="field.id || field.name"
        >
          <template #label>
            {{ field.label }} <span v-if="field.required" class="required-indicator">*</span>
          </template>
          <validation-provider
            :key="`${field.name}-select`"
            :name="field.label"
            :vid="field.name"
            :rules="field.rules || (field.required ? 'required' : '')"
            v-slot="{ errors }"
          >
            <v-select
              :id="field.id || field.name"
              v-model="localValues[field.name]"
              :options="field.options"
              :reduce="
                (option) =>
                  typeof option === 'object'
                    ? option.value !== undefined
                      ? option.value
                      : option.id
                    : option
              "
              :getOptionLabel="
                (option) =>
                  typeof option === 'object' ? option.label || option.name || option : option
              "
              :clearable="!field.required"
              :class="{ 'is-invalid': errors.length > 0 }"
              :placeholder="`Select ${field.label}`"
              @input="() => emitInput(field.name, localValues[field.name])"
            />
            <div v-if="errors.length > 0" class="invalid-feedback d-block">{{ errors[0] }}</div>
          </validation-provider>
        </b-form-group>

        <!-- Resource Field -->
        <b-form-group
          v-else-if="field.type === 'resource'"
          :label="field.label"
          :label-for="field.id || field.name"
        >
          <template #label>
            {{ field.label }} <span v-if="field.required" class="required-indicator">*</span>
          </template>
          <validation-provider
            :key="`${field.name}-resource`"
            :name="field.label"
            :vid="field.name"
            :rules="field.rules || (field.required ? 'required' : '')"
            v-slot="{ errors }"
          >
            <resource-dropdown
              :resource="field.resource"
              :id="field.id || field.name"
              v-model="localValues[field.name]"
              :error="errors.length > 0 ? errors[0] : null"
              :itemsKey="field.resource.itemsKey"
              :labelKey="field.resource.labelKey"
              :placeholder="field.placeholder || `Select ${field.label}`"
              @input="() => emitInput(field.name, localValues[field.name])"
              :params="field.params || {}"
            />
            <!-- <div v-if="errors.length > 0" class="invalid-feedback d-block">{{ errors[0] }}</div> -->
          </validation-provider>
        </b-form-group>

        <!-- Checkbox Field -->
        <b-form-group
          v-else-if="field.type === 'checkbox'"
          :label-for="field.id || field.name"
          class="my-2"
        >
          <validation-provider
            :key="`${field.name}-checkbox`"
            :name="field.label"
            :vid="field.name"
            :rules="field.rules || ''"
            v-slot="{ errors }"
          >
            <b-form-checkbox
              :id="field.id || field.name"
              :name="field.name"
              v-model="localValues[field.name]"
              :state="errors.length > 0 ? false : null"
              @input="emitInput"
            >
              {{ field.label }} <span v-if="field.required" class="required-indicator">*</span>
            </b-form-checkbox>
            <b-form-invalid-feedback>{{ errors[0] }}</b-form-invalid-feedback>
          </validation-provider>
        </b-form-group>

        <!-- File Upload Field -->
        <b-form-group
          v-else-if="field.type === 'file'"
          :label="field.label"
          :label-for="field.id || field.name"
        >
          <template #label>
            {{ field.label }} <span v-if="field.required" class="required-indicator">*</span>
          </template>
          <validation-provider
            :key="`${field.name}-file`"
            :name="field.label"
            :vid="field.name"
            :rules="field.rules || (field.required ? 'required' : '')"
            v-slot="{ errors }"
          >
            <multi-file-upload
              :id="field.id || field.name"
              v-model="localValues[field.name]"
              :placeholder="`Choose ${field.label}...`"
              :error="errors.length > 0 ? errors[0] : null"
              @input="() => emitInput(field.name, localValues[field.name])"
            />
          </validation-provider>
        </b-form-group>
      </b-col>

      <slot name="customFields" />
    </b-row>
  </div>
</template>

<script>
import MultiFileUpload from '@/components/common/form/MultiFileUpload.vue';
import ResourceDropdown from '@/components/common/ResourceDropdown.vue';
import { RESOURCES } from '@/utils/constants';
import {
  BCol,
  BFormCheckbox,
  BFormGroup,
  BFormInput,
  BFormInvalidFeedback,
  BFormTextarea,
  BRow,
} from 'bootstrap-vue';
import { ValidationProvider } from 'vee-validate';
import flatPickr from 'vue-flatpickr-component';
import vSelect from 'vue-select';
import { mask } from 'vue-the-mask';

export default {
  name: 'FormFieldsGroup',

  directives: {
    mask,
  },
  components: {
    ValidationProvider,
    BFormGroup,
    BFormInput,
    BFormInvalidFeedback,
    BFormCheckbox,
    BRow,
    BCol,
    flatPickr,
    vSelect,
    ResourceDropdown,
    BFormTextarea,
    MultiFileUpload,
  },

  props: {
    formFields: {
      type: Array,
      required: true,
    },
    value: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      localValues: {},
      dateConfig: {
        dateFormat: 'Y-m-d',
        altInput: true,
        altFormat: 'F j, Y',
        locale: {
          firstDayOfWeek: 1,
        },
      },
    };
  },

  watch: {
    value: {
      handler(val) {
        // Update localValues when external value changes
        if (val) {
          const validFieldNames = new Set(this.formFields.map((field) => field.name));

          // First, update all form fields
          Object.keys(val).forEach((key) => {
            if (
              validFieldNames.has(key) &&
              JSON.stringify(this.localValues[key]) !== JSON.stringify(val[key])
            ) {
              this.$set(this.localValues, key, val[key]);
            }
          });
        }
      },
      immediate: true,
      deep: true,
    },
  },

  methods: {
    getResource(resourceKey) {
      return RESOURCES[resourceKey] || resourceKey;
    },

    emitInput(fieldName, value) {
      // Create a copy of processed values
      const processedValues = { ...this.localValues };

      // Process number types
      this.formFields.forEach((field) => {
        if (
          field.type === 'number' &&
          processedValues[field.name] !== null &&
          processedValues[field.name] !== undefined
        ) {
          processedValues[field.name] = Number(processedValues[field.name]);
        }
      });

      // Create a new output object that includes all fields from the value prop
      const outputValues = { ...this.value };

      // Update or add fields from processedValues
      this.formFields.forEach((field) => {
        if (processedValues[field.name] !== undefined) {
          outputValues[field.name] = processedValues[field.name];
        }
      });

      // Emit update events
      this.$emit('input', outputValues);
      this.$emit('update', outputValues);
    },
  },
};
</script>

<style>
.required-indicator {
  color: #ea5455;
  font-weight: bold;
  margin-left: 2px;
}
</style>
